import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AGENTS } from "@/components/sentinel/sentinel-constants";
import { useCurrentOrgId } from "@/hooks/useSkillOS";

const SENTINEL_KEYS = {
  cycles: ["sentinel-cycles"],
  findings: ["sentinel-findings"],
  proposals: ["sentinel-proposals-v2"],
  scorecards: ["sentinel-scorecards"],
  escalations: ["sentinel-escalations"],
  remediations: ["sentinel-remediations"],
  forecasts: ["sentinel-forecasts"],
  rootCauses: ["sentinel-root-causes"],
  versionHistory: ["sentinel-version-history"],
  baselines: ["sentinel-baselines"],
  dimensionDefs: ["sentinel-dimension-defs"],
  alerts: ["sentinel-alerts"],
  rules: ["sentinel-rules"],
  audits: ["sentinel-audits"],
  reports: ["sentinel-reports"],
  scores: ["sentinel-scores"],
  orgs: ["sentinel-orgs"],
  modelRegistry: ["sentinel-model-registry"],
  intelligence: ["sentinel-intelligence"],
} as const;

export function useSentinelOrgs() {
  return useQuery({
    queryKey: SENTINEL_KEYS.orgs,
    queryFn: async () => {
      const { data, error } = await supabase.from("organizations").select("id, name");
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelFindings() {
  return useQuery({
    queryKey: SENTINEL_KEYS.findings,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_findings").select("*").order("created_at", { ascending: false }).limit(200);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelProposals() {
  return useQuery({
    queryKey: SENTINEL_KEYS.proposals,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_proposals").select("*").order("created_at", { ascending: false }).limit(200);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelScorecards() {
  return useQuery({
    queryKey: SENTINEL_KEYS.scorecards,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_scorecards").select("*").order("created_at", { ascending: false }).limit(50);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelEscalations() {
  return useQuery({
    queryKey: SENTINEL_KEYS.escalations,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_escalations").select("*").order("created_at", { ascending: false }).limit(100);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelForecasts() {
  return useQuery({
    queryKey: SENTINEL_KEYS.forecasts,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_forecasts").select("*").order("created_at", { ascending: false }).limit(50);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelRootCauses() {
  return useQuery({
    queryKey: SENTINEL_KEYS.rootCauses,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_root_causes").select("*").order("created_at", { ascending: false }).limit(50);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelVersionHistory() {
  return useQuery({
    queryKey: SENTINEL_KEYS.versionHistory,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_version_history").select("*").order("created_at", { ascending: false }).limit(200);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelAlerts(statusFilter = "all") {
  return useQuery({
    queryKey: [...SENTINEL_KEYS.alerts, statusFilter],
    queryFn: async () => {
      let query = supabase.from("sentinel_alerts").select("*").order("created_at", { ascending: false }).limit(100);
      if (statusFilter !== "all") query = query.eq("status", statusFilter);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelRules() {
  return useQuery({
    queryKey: SENTINEL_KEYS.rules,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_rules").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelScores() {
  return useQuery({
    queryKey: SENTINEL_KEYS.scores,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_scores").select("*").order("measured_at", { ascending: false }).limit(200);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelAudits() {
  return useQuery({
    queryKey: SENTINEL_KEYS.audits,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_audits").select("*").order("created_at", { ascending: false }).limit(50);
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelReports() {
  return useQuery({
    queryKey: SENTINEL_KEYS.reports,
    queryFn: async () => {
      const { data, error } = await supabase.from("sentinel_reports").select("*").order("created_at", { ascending: false }).limit(20);
      if (error) throw error;
      return data;
    },
  });
}

export function useInvalidateSentinel() {
  const qc = useQueryClient();
  return () => {
    Object.values(SENTINEL_KEYS).forEach(key => {
      qc.invalidateQueries({ queryKey: key });
    });
  };
}

export function useRunAgent() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ fnName, body }: { fnName: string; body: Record<string, unknown> }) => {
      const { data, error } = await supabase.functions.invoke(fnName, { body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateProposalState() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ id, state }: { id: string; state: string; approved_by?: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id ?? null;

      // Fetch current proposal for audit context
      const { data: current } = await supabase
        .from("sentinel_proposals")
        .select("id, org_id, state, category, title, risk_level")
        .eq("id", id)
        .maybeSingle();

      const updates: { state: string; updated_at: string; approved_at?: string; approved_by?: string | null } = {
        state,
        updated_at: new Date().toISOString(),
      };
      if (state === "approved") {
        updates.approved_at = new Date().toISOString();
        updates.approved_by = userId;
      }
      const { error } = await supabase.from("sentinel_proposals").update(updates).eq("id", id);
      if (error) throw error;

      // Audit trail (best-effort, never blocks UX)
      if (current?.org_id) {
        await supabase.from("audit_logs").insert({
          org_id: current.org_id,
          user_id: userId,
          action: `sentinel.proposal.${state}`,
          resource_type: "sentinel_proposal",
          resource_id: id,
          details: {
            previous_state: current.state,
            new_state: state,
            category: current.category,
            title: current.title,
            risk_level: current.risk_level,
          },
        });
      }
    },
    onSuccess: () => invalidate(),
  });
}

export function useApplyProposal() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ proposal_id, force }: { proposal_id: string; force?: boolean }) => {
      const { data, error } = await supabase.functions.invoke("sentinel-remediate", {
        body: { proposal_id, force: force ?? false },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

export function useAckEscalation() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("sentinel_escalations").update({ ack_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

// ── Sprint 1: extra hooks ──

export function useSentinelIntelligence() {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: [...SENTINEL_KEYS.intelligence, orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("sentinel-recommend", {
        body: { org_id: orgId },
      });
      if (error) throw error;
      return data as {
        recommendations: Array<{ id?: string; type: string; title: string; description: string; severity?: string }>;
        stats?: Record<string, number>;
        timeline?: Array<{ date: string; alerts: number; resolved: number }>;
        severity_distribution?: { critical: number; high: number; medium: number; low: number };
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSentinelModelRegistry() {
  return useQuery({
    queryKey: SENTINEL_KEYS.modelRegistry,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sentinel_model_registry")
        .select("*")
        .order("provider", { ascending: true });
      if (error) throw error;
      return data;
    },
  });
}

export function useSentinelRemediations() {
  return useQuery({
    queryKey: SENTINEL_KEYS.remediations,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sentinel_remediations")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateRule() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async (rule: {
      org_id: string; name: string; description?: string;
      metric: string; condition: string; threshold: number;
      severity: string; cooldown_minutes: number;
    }) => {
      const { error } = await supabase.from("sentinel_rules").insert(rule);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Regra criada"); },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useToggleRule() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("sentinel_rules").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAlert() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updates: { status: string; resolved_at?: string } = { status };
      if (status === "resolved") updates.resolved_at = new Date().toISOString();
      const { error } = await supabase.from("sentinel_alerts").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Alerta atualizado"); },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateModelStatus() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("sentinel_model_registry")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Status atualizado"); },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useGenerateProposals() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async (auditId: string) => {
      const { data, error } = await supabase.functions.invoke("sentinel-propose", { body: { audit_id: auditId } });
      if (error) throw error;
      return data as { proposals_created: number };
    },
    onSuccess: (data) => { invalidate(); toast.success(`${data.proposals_created} propostas geradas`); },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useGenerateReport() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ org_id, days = 7 }: { org_id: string; days?: number }) => {
      const { data, error } = await supabase.functions.invoke("sentinel-report", {
        body: { org_id, report_type: "on_demand", days },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => { invalidate(); toast.success("Relatório gerado"); },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useRunEvaluation() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("sentinel-evaluate");
      if (error) throw error;
      return data as { evaluated: number; alerts_created: number };
    },
    onSuccess: (data) => {
      invalidate();
      toast.success(`Avaliação concluída: ${data.evaluated} regras, ${data.alerts_created} alertas`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useRunAudit() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({ org_id, audit_type = "all" }: { org_id: string; audit_type?: string }) => {
      const { data, error } = await supabase.functions.invoke("sentinel-audit", {
        body: { org_id, audit_type },
      });
      if (error) throw error;
      return data as { results?: unknown[] };
    },
    onSuccess: (data) => {
      invalidate();
      toast.success(`Auditoria concluída: ${data.results?.length || 0} tipos processados`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * Generic agent runner — uses the AGENTS catalog to know which fn to invoke
 * and which payload field is required.
 */
export function useRunSentinelAgent() {
  const invalidate = useInvalidateSentinel();
  return useMutation({
    mutationFn: async ({
      agentId,
      org_id,
      auditId,
      alertId,
      incidentId,
    }: {
      agentId: string;
      org_id?: string;
      auditId?: string;
      alertId?: string;
      incidentId?: string;
    }) => {
      const agent = AGENTS.find((a) => a.id === agentId);
      if (!agent) throw new Error(`Agente desconhecido: ${agentId}`);

      const body: Record<string, unknown> = {};
      if (org_id) body.org_id = org_id;

      if (agent.needs === "audit_id") {
        if (!auditId) throw new Error("Nenhuma auditoria disponível para gerar propostas.");
        body.audit_id = auditId;
      }
      if (agent.needs === "incident_id") {
        if (!alertId && !incidentId) throw new Error("Nenhum alerta disponível para análise de causa raiz.");
        body.incident_id = incidentId ?? alertId;
      }
      if (agent.needs === "alert_id") {
        if (!alertId) throw new Error("Nenhum alerta disponível para remediação.");
        body.alert_id = alertId;
        body.action = "acknowledge_and_monitor";
      }

      const { data, error } = await supabase.functions.invoke(agent.fn, { body });
      if (error) throw error;
      return { agent, data };
    },
    onSuccess: ({ agent }) => {
      invalidate();
      toast.success(`${agent.name} executado com sucesso`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
